import React, { memo, useCallback } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { FaLock, FaUnlock, FaInfoCircle } from 'react-icons/fa';
import { ProductAutoCompleteCell } from './ProductSelector';
import axiosInstance from '../services/axiosInstance';
import { handleApiError } from './apiErrorHandler';

/**
 * InvoiceItemRow
 *
 * Renders all editable cells for a single invoice line item.
 * Wrapped in React.memo with a custom equality check so it only
 * re-renders when this specific row's data changes — not when
 * unrelated state elsewhere in InvoiceList updates.
 *
 * Each cell type is its own named sub-component so PrimeReact's
 * Column body prop receives a stable function reference.
 */

// ── Individual cell components ────────────────────────────────────────────────

export const ProductCell = memo(({ rowData, isReadOnly, handleItemChange, inputRefMap }) => {
    if (isReadOnly) return <span>{rowData.product?.name || ''}</span>;
    return (
        <div id={rowData.id}>
            <ProductAutoCompleteCell
                rowData={rowData}
                onValueChange={handleItemChange}
                inputRefMap={inputRefMap}
            />
        </div>
    );
});
ProductCell.displayName = 'ProductCell';

export const QuantityCell = memo(({ rowData, isReadOnly, handleItemChange, qtyBump }) => {
    if (isReadOnly) return <span>{rowData.quantity || ''}</span>;
    return (
        <InputNumber
            key={`qty-${rowData.id}-${rowData.batch_number || 'base'}-${qtyBump || 0}`}
            value={rowData.quantity}
            onValueChange={(e) => handleItemChange(rowData.id, 'quantity', e.value)}
            placeholder="Quantity"
            required
        />
    );
});
QuantityCell.displayName = 'QuantityCell';

export const DurationCell = memo(({ rowData, isReadOnly, handleItemChange }) => {
    if (isReadOnly) return <span>{rowData.duration || ''}</span>;
    return (
        <InputNumber
            value={rowData.duration}
            onValueChange={(e) => handleItemChange(rowData.id, 'duration', e.target.value)}
            placeholder="Duration"
        />
    );
});
DurationCell.displayName = 'DurationCell';

export const PriceCell = memo(({ rowData, isReadOnly, handleItemChange, priceEditable, overrideApproval, setPasswordPurpose, setPasswordDialogHeader, setPasswordDialogVisible }) => {
    if (isReadOnly) return <span>{rowData.price || 0.00}</span>;
    return (
        <div className="price-field" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <InputText
                id="price"
                value={rowData.price}
                onChange={(e) => handleItemChange(rowData.id, 'price', e.target.value)}
                disabled={!priceEditable}
                placeholder="Price"
                style={{ paddingRight: '30px', width: '100%', minWidth: '80px' }}
            />
            {overrideApproval && !priceEditable ? (
                <FaLock
                    onClick={() => {
                        if (!isReadOnly) {
                            setPasswordPurpose('override');
                            setPasswordDialogHeader('Enter Price Override Password');
                            setPasswordDialogVisible(true);
                        }
                    }}
                    style={{ position: 'absolute', right: '10px', cursor: 'pointer', color: '#888' }}
                    size={15}
                    title="Click to unlock price"
                />
            ) : (
                <FaUnlock
                    style={{ position: 'absolute', right: '10px', color: '#4CAF50' }}
                    size={15}
                    title="Price Overriding enabled"
                />
            )}
        </div>
    );
});
PriceCell.displayName = 'PriceCell';

export const TotalCell = memo(({ rowData, isReadOnly, handleItemChange }) => {
    if (isReadOnly) return <span>{rowData.total || ''}</span>;
    return (
        <InputNumber
            value={rowData.total}
            onValueChange={(e) => handleItemChange(rowData.id, 'total', e.target.value)}
            placeholder="Total Price"
            required
            readOnly
        />
    );
});
TotalCell.displayName = 'TotalCell';

export const BatchCell = memo(({
    rowData,
    isReadOnly,
    manualBatchEnabledFlag,
    manualBatchMap,
    setManualBatchMap,
    batchOptionsMap,
    setBatchOptionsMap,
    excludedBatchesMap,
    fetchBatchAllocations,
    setInvoice,
    showExcludedDialog,
}) => {
    const allowManual = manualBatchEnabledFlag[rowData.id];
    const isManual    = manualBatchMap[rowData.id];
    const batches     = batchOptionsMap[rowData.id] || [];

    const handleManualToggle = useCallback(async (e) => {
        const newValue = e.checked;
        setManualBatchMap(prev => ({ ...prev, [rowData.id]: newValue }));
        if (newValue) {
            try {
                const res = await axiosInstance.post('/api/inventory/get-available-batches/', {
                    product_id: rowData.product?.id,
                });
                setBatchOptionsMap(prev => ({ ...prev, [rowData.id]: res.data.batches || [] }));
            } catch (err) {
                handleApiError(err);
            }
        } else {
            fetchBatchAllocations(rowData.product?.id, rowData.quantity, rowData.id, rowData.product);
        }
    }, [rowData.id, rowData.product, rowData.quantity, setManualBatchMap, setBatchOptionsMap, fetchBatchAllocations]);

    const handleBatchSelect = useCallback((e) => {
        const selected = e.value;
        setInvoice(prev => ({
            ...prev,
            items: prev.items.map(item =>
                item.id === rowData.id ? {
                    ...item,
                    batch_number: selected.batch_number,
                    expiry_date:  selected.expiry_date,
                    inventory:    selected.id,
                    price:        selected.price_list__selling_price,
                    price_list:   selected.price_list,
                    total:        item.quantity * selected.price_list__selling_price,
                } : item
            ),
        }));
    }, [rowData.id, setInvoice]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {allowManual && (
                <div className="flex align-items-center" style={{ transform: 'scale(0.8)', marginBottom: '0.25rem' }}>
                    <Checkbox
                        inputId={`manual-${rowData.id}`}
                        checked={!!isManual}
                        onChange={handleManualToggle}
                    />
                    <label htmlFor={`manual-${rowData.id}`} className="ml-2">Manual</label>
                </div>
            )}
            {isManual ? (
                <Dropdown
                    value={batches.find(b => b.batch_number === rowData.batch_number) || null}
                    options={batches}
                    onChange={handleBatchSelect}
                    optionLabel="batch_number"
                    placeholder="Select Batch"
                    className="w-full"
                />
            ) : (
                <span style={{ display: 'flex', alignItems: 'center' }}>
                    {rowData.batch?.batch_number || rowData.batch_number || ''}
                    {excludedBatchesMap[rowData.id]?.length > 0 && (
                        <FaInfoCircle
                            style={{ marginLeft: 6, cursor: 'pointer', color: '#f39c12' }}
                            title="View blocked batches"
                            onClick={() => showExcludedDialog(rowData.id)}
                        />
                    )}
                </span>
            )}
        </div>
    );
});
BatchCell.displayName = 'BatchCell';

export const ExpiryCell = memo(({ rowData }) => (
    <span>{rowData.expiry_date ? new Date(rowData.expiry_date).toLocaleDateString() : ''}</span>
));
ExpiryCell.displayName = 'ExpiryCell';

export const ActionCell = memo(({ rowData, removeItem }) => (
    <Button
        type="button"
        icon="pi pi-trash"
        className="p-button-danger"
        onClick={() => removeItem(rowData.id)}
    />
));
ActionCell.displayName = 'ActionCell';
